/**
 * API response parsers for LOHP data sources.
 *
 * Each parser transforms a raw API response (snake_case REST or GraphQL shape)
 * into the typed data structures used by LOHP components.
 */
import type {
    EnrichedSkillsData,
    TrendingSkillsData,
    TrendingSkillsVariant,
    TrendingTopic,
    CategoryTopics,
    EnrollmentStats,
    TestimonialsData,
    Testimonial,
    TestimonialsGroup,
    PartnersBannerData,
    Academy,
} from './types';
import type {
    DynamicWebContentPartnersBannerQuery,
    DynamicWebContentTestimonialsQuery,
    MarketingSplitCardsCarouselV1Query,
    CareerAcceleratorsByLanguageQuery,
} from '@/generated/graphql';
import {LanguageCode} from '@/generated/graphql';
import {extractSlugFromAccelerator} from '@udemy-v2/career-accelerators/accelerator-utils';
import type {LanguageCode as SharedLanguageCode} from '@udemy-v2/graphql';

// ---------- Enriched Skills (GraphQL) ----------

/**
 * copy/pasta from frontends-homepage:
 * CMS fields (e.g. `headline`) may arrive wrapped in an HTML tag (e.g. `<p>…</p>`
 * or `<h3>…</h3>`) and may include trailing empty paragraphs. When the consuming
 * component renders the value via `dangerouslySetInnerHTML` inside its own
 * block-level element (a heading, or a design-system `<p>` wrapper), the CMS's
 * outer tag must be stripped — otherwise the result is a `<p>`/`<h*>` nested
 * inside another `<p>`, which HTML5 parsing forbids. The browser silently
 * auto-closes the outer element to recover, producing a DOM that doesn't match
 * what React expects and throwing a hydration mismatch (confirmed via direct
 * SSR/RSC-flight inspection for the Testimonials attribution description —
 * both were byte-identical and correct, so the divergence happens purely in
 * the browser's HTML parser, not in the server-sent data).
 */
function stripWrappingHeadingTags(html: string): string {
    return html
        .replace(/^<(h[1-6]|p)[^>]*>([\s\S]*?)<\/\1>/, '$2') // Remove outer <p> or <h*> wrapper
        .replace(/\s*<p>\s*<\/p>\s*$/g, '') // Remove trailing empty <p></p> tags
        .trim();
}

/**
 * Adapt the MarketingSplitCardsCarouselV1 GraphQL response into EnrichedSkillsData.
 * Returns null if the response is empty or malformed.
 */
export function adaptEnrichedSkillsData(
    data: MarketingSplitCardsCarouselV1Query | null | undefined,
): EnrichedSkillsData | null {
    const carousel = data?.marketingSplitCardsCarouselV1;
    if (!carousel || !carousel.cards?.length) return null;

    const rawHeadline = carousel.header?.headline ?? '';
    const supportingText = carousel.header?.supportingText ?? '';

    return {
        header: {
            headline: stripWrappingHeadingTags(String(rawHeadline)),
            supportingText,
        },
        cards: carousel.cards
            .filter((card): card is NonNullable<typeof card> => Boolean(card))
            .map((card) => ({
                title: card.title ?? '',
                url: String(card.url ?? ''),
                image: {url: String(card.image?.url ?? '')},
            })),
    };
}

// ---------- Trending Skills (REST waterfall) ----------

interface RawSdTag {
    id: number;
    title: string;
    url: string;
    type: string;
}

interface RawCategoryResponse {
    stats?: Array<{sd_tag: RawSdTag}>;
}

/** Category metadata for a trending-skills category. */
export interface TrendingCategoryMeta {
    id: number;
    name: string;
}

export const TRENDING_CATEGORIES: TrendingCategoryMeta[] = [
    {id: 288, name: 'Development'},
    {id: 269, name: 'Design'},
    {id: 268, name: 'Business'},
];

export const INDIA_TRENDING_CATEGORIES: TrendingCategoryMeta[] = [
    {id: 288, name: 'Development'},
    {id: 269, name: 'Data Analytics'},
    {id: 268, name: 'Career Certifications'},
];

export const JAPAN_TRENDING_CATEGORIES: TrendingCategoryMeta[] = [
    {id: 288, name: 'Development'},
    {id: 268, name: 'Business skills'},
    {id: 294, name: 'Career Certifications'},
];

export const INDIA_CATEGORY_TOPIC_OVERRIDES: Record<number, number[]> = {
    269: [6730, 7862, 6740],
    268: [7224, 24774, 46868],
};

export const JAPAN_CATEGORY_TOPIC_OVERRIDES: Record<number, number[]> = {
    288: [7380, 8322, 118634],
    268: [7224, 6740, 7862],
    294: [24774, 157986, 46868],
};

export function getTrendingCategories(
    variant: TrendingSkillsVariant = 'default',
): TrendingCategoryMeta[] {
    if (variant === 'japan') return JAPAN_TRENDING_CATEGORIES;
    if (variant === 'india') return INDIA_TRENDING_CATEGORIES;
    return TRENDING_CATEGORIES;
}

export function getCategoryTopicOverrides(
    variant: TrendingSkillsVariant = 'default',
): Record<number, number[]> {
    if (variant === 'japan') return JAPAN_CATEGORY_TOPIC_OVERRIDES;
    if (variant === 'india') return INDIA_CATEGORY_TOPIC_OVERRIDES;
    return {};
}

/**
 * Build TrendingSkillsData from the category and enrollment-stat responses.
 * Top-skill enrollment is looked up from `enrollmentStats` by the fixed top-skill ID.
 */
export function parseTrendingSkillsResponse(
    categoryResponses: RawCategoryResponse[],
    enrollmentStats: EnrollmentStats,
    topTag: RawSdTag,
    categories: TrendingCategoryMeta[] = TRENDING_CATEGORIES,
): TrendingSkillsData {
    const categoryTopics: CategoryTopics[] = categoryResponses.map((resp, idx) => {
        const meta = categories[idx];
        const topics: TrendingTopic[] = (resp.stats ?? []).slice(0, 5).map((tag) => ({
            id: tag.sd_tag.id,
            title: tag.sd_tag.title,
            url: tag.sd_tag.url,
            type: tag.sd_tag.type,
        }));
        return {categoryId: meta.id, categoryName: meta.name, topics};
    });

    const topSkill = {
        ...topTag,
        enrollmentCount: enrollmentStats[topTag.id]?.stat_value ?? 0,
    };

    return {topSkill, categoryTopics, enrollmentStats};
}

// ---------- Testimonials (GraphQL) ----------

const hasRequiredTestimonialFields = (group: TestimonialsGroup): boolean =>
    Boolean(
        group.description &&
            group.contentUrl &&
            group.contentUrlText &&
            group.trackingId &&
            group.contents.length > 0,
    );

const hasRequiredPersonFields = (person: Testimonial): boolean =>
    Boolean(person.heading && person.description && person.image.url);

/**
 * Adapt the DynamicWebContentTestimonials GraphQL response into TestimonialsData.
 * Returns null if the response is empty or malformed, or if every card is incomplete.
 */
export function adaptTestimonialsData(
    data: DynamicWebContentTestimonialsQuery | null | undefined,
): TestimonialsData | null {
    const content = data?.dynamicWebContent;
    if (!content || !content.contents?.length) return null;

    const contents = content.contents
        .filter((g): g is NonNullable<typeof g> => Boolean(g))
        .map(
            (group): TestimonialsGroup => ({
                labelText: group.labelText ?? null,
                description: group.description ?? '',
                contentUrl: String(group.contentUrl ?? ''),
                contentUrlText: group.contentUrlText ?? '',
                contents: (group.contents ?? [])
                    .filter((t): t is NonNullable<typeof t> => Boolean(t))
                    .map(
                        (t): Testimonial => ({
                            heading: stripWrappingHeadingTags(t.heading ?? ''),
                            description: stripWrappingHeadingTags(t.description ?? ''),
                            image: {url: String(t.image?.url ?? '')},
                        }),
                    )
                    .filter(hasRequiredPersonFields),
                trackingId: group.trackingId ?? '',
            }),
        )
        .filter(hasRequiredTestimonialFields);

    if (!contents.length) return null;

    return {heading: content.heading ?? '', contents};
}

// ---------- Partners Banner ----------

export function adaptPartnersBannerData(
    data: DynamicWebContentPartnersBannerQuery | null | undefined,
): PartnersBannerData | null {
    const content = data?.dynamicWebContent;
    if (!content?.contents?.length) return null;

    const contents = content.contents
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.image?.url))
        .map((c) => ({
            image: {
                url: String(c.image?.url ?? ''),
                altText: c.image?.altText ?? '',
            },
        }));

    if (!contents.length) return null;

    return {heading: content.heading ?? '', contents};
}

// ---------- Career Academies ----------

// LanguageCode only carries a region for zh/pt; every other locale maps to its base language.
const REGION_AWARE_LANGUAGES = ['zh', 'pt'];

export const resolveLanguageCode = (locale: string): LanguageCode => {
    const [lang, region] = locale.split('_');
    const candidate = REGION_AWARE_LANGUAGES.includes(lang.toLowerCase())
        ? `${lang}_${region}`.toUpperCase()
        : lang.toUpperCase();

    return (
        Object.values(LanguageCode).includes(candidate as LanguageCode)
            ? candidate
            : lang.toUpperCase()
    ) as LanguageCode;
};

type CareerAcceleratorItem =
    CareerAcceleratorsByLanguageQuery['careerAccelerators']['items'][number];

// The API sends the literal string "0" (not empty/null) when there's no salary data.
// Strips everything except digits, dots, and minus signs (e.g. "$95,000" -> "95000").
const NON_NUMERIC_CHARS = /[^0-9.-]/g;
const hasSalaryData = (averageSalary: string | null | undefined) =>
    !!averageSalary && Number(averageSalary.replace(NON_NUMERIC_CHARS, '')) > 0;

export function parseCareerAccelerator(item: CareerAcceleratorItem): Academy {
    const durationInSeconds = Number(item.stats?.durationInSeconds ?? 0);

    return {
        slug: extractSlugFromAccelerator(item.slug, item.language as unknown as SharedLanguageCode),
        title: item.career.name,
        image: item.career.image.wide,
        average_salary: hasSalaryData(item.career.averageSalary)
            ? item.career.averageSalary
            : undefined,
        open_roles:
            item.career.openJobPositionsCount > 0
                ? item.career.openJobPositionsCount.toLocaleString()
                : undefined,
        total_hours: durationInSeconds > 0 ? Math.round(durationInSeconds / 3600) : undefined,
        avg_rating: Number(item.stats?.averageRating ?? 0),
        num_reviews: Number(item.stats?.numberOfRatings ?? 0),
    };
}
